import { Injectable } from '@angular/core';
import { CanvasService } from './canvas.service';
import { SvgEditExtension } from '@models/svg-edit-extension';
import { CanvasInputMode } from './canvas-input-mode.service';
import { BarExtensionService } from './bar-extension.service';
import { InputValueExtensionService } from './inputValue-extension.service';
import { ToggleExtensionService } from './toggle-extension.service';
import { ButtonExtensionService } from './button-extension.service';
import { StatusIndicatorExtensionService } from './status-indicator-extension.service';
import { OutputValueExtensionService } from './outputValue-extension.service';
import { ConnectorExtensionService } from './connector/connector-extension.service';
import { PathService } from './path.service';
import extGrid from '@assets/lib/extensions/ext-grid.js';

@Injectable({ providedIn: 'root' })
export class CanvasExtensionsService {
    private isExtensionAdded: boolean = false;
    extensionMap: Partial<Record<CanvasInputMode, SvgEditExtension>>;

    constructor(
        private canvas: CanvasService,
        private barExtension: BarExtensionService,
        private inputValueExtension: InputValueExtensionService,
        private outputValueExtension: OutputValueExtensionService,
        private toggleExtension: ToggleExtensionService,
        private buttonExtension: ButtonExtensionService,
        private statusExtension: StatusIndicatorExtensionService,
        private connectorExtension: ConnectorExtensionService,
        private pathService: PathService
    ) {
        this.extensionAddedHandler = this.extensionAddedHandler.bind(this);
    }

    public init() {
        this.canvas.getCanvas().bind('extension_added', this.extensionAddedHandler);
        this.extensionMap = {
            [CanvasInputMode.BAR]: this.barExtension,
            [CanvasInputMode.INPUT_VALUE]: this.inputValueExtension,
            [CanvasInputMode.OUTPUT_VALUE]: this.outputValueExtension,
            [CanvasInputMode.TOGGLE]: this.toggleExtension,
            [CanvasInputMode.BUTTON]: this.buttonExtension,
            [CanvasInputMode.STATUS_INDICATOR]: this.statusExtension,
            [CanvasInputMode.CONNECTOR]: this.connectorExtension,
            [CanvasInputMode.PATH]: this.pathService,
        };
    }

    public install(extension: SvgEditExtension, initParams: any = {}): Promise<void> {
        return this.canvas.getCanvas().addExtension(extension.name, extension.init, initParams);
    }

    public installControlersExtensions() {
        if (this.isExtensionAdded) return;
        for (const extension of Object.values(this.extensionMap)) {
            this.install(extension);
        }
        this.isExtensionAdded = true;
    }

    public installLegacyExtensions() {
        this.canvas.addExtension(
            {
                name: extGrid.name,
                init: extGrid.init && extGrid.init.bind(this),
                addEventListeners: undefined,
            },
            { langParam: 'en' }
        );
        this.canvas.getCanvas().call('extensions_added');
    }

    private extensionAddedHandler(win, ext) {
        if (!ext) {
            return undefined;
        }
        let cbCalled = false;

        const runCallback = () => {
            if (ext.callback && !cbCalled) {
                cbCalled = true;
                ext.callback.call(this);
            }
        };

        return runCallback();
    }

    updateInputUnit(foreignObject, units: string) {
        const foreignObjectType = foreignObject.getAttribute('type');
        if (foreignObjectType === CanvasInputMode.INPUT_VALUE) {
            this.inputValueExtension.udpateInputUnit(foreignObject, units);
        } else if (foreignObjectType === CanvasInputMode.OUTPUT_VALUE) {
            this.outputValueExtension.udpateInputUnit(foreignObject, units);
        }
    }

    attachEventListeners(foreignObject) {
        const foreignObjectType = foreignObject.getAttribute('type');

        const extension = this.extensionMap[foreignObjectType];
        if (extension) {
            extension.addEventListeners(foreignObject);
        } else {
            console.warn(`Unsupported foreignObjectType: ${foreignObjectType}`);
        }
    }
}
